import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { authApi } from '../auth/api';
import '../styles/modules-supervisor-organizations.css';
import { PrototypeIcon } from './PrototypeIcon';
import {
  PlatformApiError,
  platformApi,
  type PlatformAdministratorCandidateResult,
  type PlatformAdministratorProvisioningInput,
  type PlatformOrganizationCreateInput,
  type PlatformOrganizationFormType,
  type PlatformOrganizationResult,
} from './platform-api';

const ORGANIZATION_TYPE_LABELS: Readonly<Record<PlatformOrganizationFormType, string>> = {
  COMPANY: 'Société',
  ASSOCIATION: 'Association',
  PUBLIC_INSTITUTION: 'Collectivité',
  INDIVIDUAL: 'Particulier',
};

const COUNTRY_OPTIONS = [
  ['FR', 'France'],
  ['BE', 'Belgique'],
  ['CH', 'Suisse'],
  ['LU', 'Luxembourg'],
  ['DE', 'Allemagne'],
  ['ES', 'Espagne'],
  ['IT', 'Italie'],
  ['PT', 'Portugal'],
  ['NL', 'Pays-Bas'],
  ['GB', 'Royaume-Uni'],
  ['IE', 'Irlande'],
  ['AT', 'Autriche'],
  ['DK', 'Danemark'],
  ['SE', 'Suède'],
  ['NO', 'Norvège'],
  ['FI', 'Finlande'],
  ['PL', 'Pologne'],
  ['CZ', 'Tchéquie'],
  ['RO', 'Roumanie'],
  ['GR', 'Grèce'],
  ['HR', 'Croatie'],
  ['US', 'États-Unis'],
  ['CA', 'Canada'],
] as const;

interface OrganizationDraft {
  readonly displayName: string;
  readonly formType: PlatformOrganizationFormType;
  readonly legalIdentifier: string;
  readonly logoUrl: string;
  readonly addressLine1: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
  readonly websiteUrl: string;
  readonly customCountryCode: string;
}

const EMPTY_ORGANIZATION: OrganizationDraft = {
  displayName: '',
  formType: 'COMPANY',
  legalIdentifier: '',
  logoUrl: '',
  addressLine1: '',
  postalCode: '',
  city: '',
  countryCode: 'FR',
  websiteUrl: '',
  customCountryCode: '',
};

type DialogStage = 'ORGANIZATION' | 'ADMINISTRATOR';
type AdministratorMode = 'EXISTING' | 'NEW';

export function SupervisorOrganizations(): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [organizations, setOrganizations] = useState<readonly PlatformOrganizationResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialogStage, setDialogStage] = useState<DialogStage | null>(null);
  const [targetOrganization, setTargetOrganization] = useState<PlatformOrganizationResult | null>(
    null,
  );
  const [organizationDraft, setOrganizationDraft] = useState<OrganizationDraft>(EMPTY_ORGANIZATION);
  const [creatingOrganization, setCreatingOrganization] = useState(false);

  async function loadOrganizations(search: string, cancelled?: () => boolean): Promise<void> {
    setError(null);
    try {
      const result = await platformApi.searchOrganizations(search);
      if (!cancelled?.()) setOrganizations(result);
    } catch {
      if (!cancelled?.()) setError('Les organisations ne peuvent pas être chargées.');
    } finally {
      if (!cancelled?.()) setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      void loadOrganizations(query, () => cancelled);
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  function beginOrganizationCreation(): void {
    setNotice(null);
    setError(null);
    setOrganizationDraft(EMPTY_ORGANIZATION);
    setTargetOrganization(null);
    setDialogStage('ORGANIZATION');
  }

  function beginAdministratorAttachment(organization: PlatformOrganizationResult): void {
    setNotice(null);
    setError(null);
    setTargetOrganization(organization);
    setDialogStage('ADMINISTRATOR');
  }

  function closeDialog(): void {
    if (creatingOrganization) return;
    setDialogStage(null);
    setTargetOrganization(null);
  }

  async function createOrganization(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (creatingOrganization) return;
    setCreatingOrganization(true);
    setError(null);
    try {
      const countryCode =
        organizationDraft.countryCode === 'OTHER'
          ? organizationDraft.customCountryCode.trim().toUpperCase()
          : organizationDraft.countryCode;
      const input: PlatformOrganizationCreateInput = {
        displayName: organizationDraft.displayName,
        formType: organizationDraft.formType,
        legalIdentifier:
          organizationDraft.formType === 'INDIVIDUAL' ||
          organizationDraft.legalIdentifier.trim() === ''
            ? null
            : organizationDraft.legalIdentifier,
        logoUrl: organizationDraft.logoUrl.trim() === '' ? null : organizationDraft.logoUrl,
        addressLine1: organizationDraft.addressLine1,
        postalCode: organizationDraft.postalCode,
        city: organizationDraft.city,
        countryCode,
        websiteUrl:
          organizationDraft.websiteUrl.trim() === '' ? null : organizationDraft.websiteUrl,
      };
      const { csrfToken } = await authApi.csrfToken();
      const created = await platformApi.createOrganization(input, csrfToken);
      setTargetOrganization(created);
      setDialogStage('ADMINISTRATOR');
      setNotice(
        `${created.displayName} est créée en brouillon. Ajoutez son premier Administrateur pour finaliser le rattachement.`,
      );
      await loadOrganizations(query);
    } catch (caught) {
      setError(provisioningErrorMessage(caught, 'L’organisation n’a pas pu être créée.'));
    } finally {
      setCreatingOrganization(false);
    }
  }

  async function administratorAttached(message: string): Promise<void> {
    setNotice(message);
    setDialogStage(null);
    setTargetOrganization(null);
    await loadOrganizations(query);
  }

  return (
    <main className="app-content supervisor-workspace supervisor-organizations">
      <header className="dashboard-heading supervisor-organizations__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Provisionnement plateforme</p>
          <h1>Organisations</h1>
          <p className="dashboard-heading__intro">
            Créez les organisations Tosho et rattachez leurs Administrateurs sans ouvrir leurs
            données métier.
          </p>
        </div>
        <button
          className="supervisor-button supervisor-button--primary"
          type="button"
          onClick={beginOrganizationCreation}
        >
          Créer une organisation
        </button>
      </header>

      <section
        className="supervisor-organizations__search"
        aria-label="Recherche des organisations"
      >
        <label>
          <span>Rechercher une organisation</span>
          <div className="supervisor-organizations__search-field">
            <PrototypeIcon name="search" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Nom, ville ou identifiant légal…"
              autoComplete="off"
            />
          </div>
        </label>
        <small>
          Les 12 meilleures correspondances sont proposées au fil de la saisie. Saisissez pour
          affiner.
        </small>
      </section>

      {notice ? (
        <p className="supervisor-notice tosho-feedback is-success" role="status">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p className="supervisor-danger-error tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? (
        <p className="supervisor-dashboard__status">Recherche des organisations…</p>
      ) : null}

      {!loading ? (
        <section className="supervisor-panel supervisor-organizations__panel">
          <header className="supervisor-panel__header">
            <div>
              <p className="dashboard-panel__eyebrow">Répertoire plateforme</p>
              <h2>Organisations existantes</h2>
            </div>
            <span className="supervisor-users__count">
              {organizations.length.toLocaleString('fr-FR')} correspondance(s) affichée(s)
            </span>
          </header>

          {organizations.length === 0 ? (
            <div className="dashboard-empty-state dashboard-empty-state--compact">
              <span className="dashboard-empty-state__mark" aria-hidden="true">
                <PrototypeIcon name="network" />
              </span>
              <strong>Aucune organisation correspondante</strong>
              <p>Modifiez la recherche ou créez une nouvelle organisation.</p>
            </div>
          ) : (
            <ul className="supervisor-organizations__list">
              {organizations.map((organization) => (
                <OrganizationRow
                  key={organization.id}
                  organization={organization}
                  onAttach={() => beginAdministratorAttachment(organization)}
                />
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {dialogStage === 'ORGANIZATION' ? (
        <OrganizationDialog
          draft={organizationDraft}
          pending={creatingOrganization}
          onChange={setOrganizationDraft}
          onSubmit={(event) => void createOrganization(event)}
          onClose={closeDialog}
        />
      ) : null}

      {dialogStage === 'ADMINISTRATOR' && targetOrganization !== null ? (
        <AdministratorDialog
          organization={targetOrganization}
          onDone={(message) => void administratorAttached(message)}
          onClose={closeDialog}
        />
      ) : null}
    </main>
  );
}

function OrganizationRow({
  organization,
  onAttach,
}: {
  readonly organization: PlatformOrganizationResult;
  readonly onAttach: () => void;
}): React.JSX.Element {
  const inactive = organization.status === 'SUSPENDED' || organization.status === 'ARCHIVED';
  return (
    <li className="supervisor-organizations__row">
      <div className="supervisor-organizations__identity">
        <span className="supervisor-organizations__mark" aria-hidden="true">
          {organization.displayName.slice(0, 1).toUpperCase()}
        </span>
        <span>
          <strong>{organization.displayName}</strong>
          <small>
            {organizationTypeLabel(organization.type)} ·{' '}
            {organization.city ?? 'Ville non renseignée'} ·{' '}
            {organization.countryCode ?? 'Pays non renseigné'}
          </small>
        </span>
      </div>
      <div className="supervisor-organizations__meta">
        <span
          className={`supervisor-status supervisor-status--${organization.status.toLowerCase()}`}
        >
          {organizationStatusLabel(organization.status)}
        </span>
        {organization.legalIdentifier ? <small>{organization.legalIdentifier}</small> : null}
      </div>
      <button
        className="supervisor-button supervisor-button--secondary"
        type="button"
        disabled={inactive}
        onClick={onAttach}
      >
        Rattacher un Administrateur
      </button>
    </li>
  );
}

function OrganizationDialog({
  draft,
  pending,
  onChange,
  onSubmit,
  onClose,
}: {
  readonly draft: OrganizationDraft;
  readonly pending: boolean;
  readonly onChange: (draft: OrganizationDraft) => void;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const isIndividual = draft.formType === 'INDIVIDUAL';
  const selectedCountry =
    draft.countryCode === 'OTHER' ? draft.customCountryCode : draft.countryCode;
  const showLegalIdentifier = !isIndividual;

  function patch(values: Partial<OrganizationDraft>): void {
    onChange({ ...draft, ...values });
  }

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop supervisor-provisioning-backdrop"
      aria-labelledby="create-organization-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          onClick={onClose}
          disabled={pending}
          aria-label="Fermer"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel supervisor-provisioning-dialog">
        <header className="supervisor-provisioning-dialog__header tosho-modal-header">
          <div>
            <p>Nouvelle organisation</p>
            <h2 id="create-organization-title">Informations de l’organisation</h2>
          </div>
        </header>
        <form className="supervisor-provisioning-form" onSubmit={onSubmit}>
          <div className="supervisor-provisioning-form__grid">
            <label className="is-wide">
              <span>Nom de l’organisation</span>
              <input
                required
                maxLength={160}
                value={draft.displayName}
                onChange={(event) => patch({ displayName: event.currentTarget.value })}
              />
            </label>
            <label>
              <span>Type</span>
              <select
                value={draft.formType}
                onChange={(event) =>
                  patch({
                    formType: event.currentTarget.value as PlatformOrganizationFormType,
                    legalIdentifier:
                      event.currentTarget.value === 'INDIVIDUAL' ? '' : draft.legalIdentifier,
                  })
                }
              >
                {Object.entries(ORGANIZATION_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Pays</span>
              <select
                value={draft.countryCode}
                onChange={(event) => patch({ countryCode: event.currentTarget.value })}
              >
                {COUNTRY_OPTIONS.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
                <option value="OTHER">Autre pays…</option>
              </select>
            </label>
            {draft.countryCode === 'OTHER' ? (
              <label>
                <span>Code pays ISO</span>
                <input
                  required
                  maxLength={2}
                  value={draft.customCountryCode}
                  placeholder="JP"
                  onChange={(event) =>
                    patch({
                      customCountryCode: event.currentTarget.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, '')
                        .slice(0, 2),
                    })
                  }
                />
              </label>
            ) : null}
            {showLegalIdentifier ? (
              <label>
                <span>{selectedCountry === 'FR' ? 'SIRET' : 'Identifiant légal'}</span>
                <input
                  maxLength={64}
                  value={draft.legalIdentifier}
                  onChange={(event) => patch({ legalIdentifier: event.currentTarget.value })}
                />
              </label>
            ) : null}
            <label className="is-wide">
              <span>Adresse postale</span>
              <input
                required
                maxLength={255}
                value={draft.addressLine1}
                onChange={(event) => patch({ addressLine1: event.currentTarget.value })}
              />
            </label>
            <label>
              <span>Code postal</span>
              <input
                required
                maxLength={32}
                value={draft.postalCode}
                onChange={(event) => patch({ postalCode: event.currentTarget.value })}
              />
            </label>
            <label>
              <span>Ville</span>
              <input
                required
                maxLength={120}
                value={draft.city}
                onChange={(event) => patch({ city: event.currentTarget.value })}
              />
            </label>
            <label className="is-wide">
              <span>
                Site web <small>facultatif</small>
              </span>
              <input
                type="url"
                maxLength={2048}
                placeholder="https://…"
                value={draft.websiteUrl}
                onChange={(event) => patch({ websiteUrl: event.currentTarget.value })}
              />
            </label>
            <label className="is-wide">
              <span>
                Logo <small>URL facultative pour l’instant</small>
              </span>
              <input
                type="url"
                maxLength={2048}
                placeholder="https://…"
                value={draft.logoUrl}
                onChange={(event) => patch({ logoUrl: event.currentTarget.value })}
              />
            </label>
          </div>
          <div className="supervisor-provisioning-dialog__actions">
            <button
              className="supervisor-button supervisor-button--secondary"
              type="button"
              disabled={pending}
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              className="supervisor-button supervisor-button--primary"
              type="submit"
              disabled={pending}
            >
              {pending ? 'Création…' : 'Continuer vers l’Administrateur'}
            </button>
          </div>
        </form>
      </section>
    </ModalBackdrop>
  );
}

function AdministratorDialog({
  organization,
  onDone,
  onClose,
}: {
  readonly organization: PlatformOrganizationResult;
  readonly onDone: (message: string) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const [mode, setMode] = useState<AdministratorMode>('EXISTING');
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<readonly PlatformAdministratorCandidateResult[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'EXISTING' || query.trim().length < 2) {
      setCandidates([]);
      setCandidateLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCandidateLoading(true);
    const timer = window.setTimeout(() => {
      void platformApi
        .searchAdministratorCandidates(query)
        .then((result) => {
          if (!cancelled) setCandidates(result);
        })
        .catch(() => {
          if (!cancelled) setError('La recherche de comptes n’est pas disponible.');
        })
        .finally(() => {
          if (!cancelled) setCandidateLoading(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [mode, query]);

  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => candidate.userId === selectedUserId) ?? null,
    [candidates, selectedUserId],
  );
  const restoringArchived = selectedCandidate?.accountStatus === 'ARCHIVED';

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;
    if (mode === 'EXISTING' && (selectedCandidate === null || !selectedCandidate.eligible)) {
      setError('Sélectionnez un compte éligible.');
      return;
    }

    setPending(true);
    setError(null);
    try {
      let input: PlatformAdministratorProvisioningInput;
      let restoring = false;
      if (mode === 'EXISTING') {
        if (selectedCandidate === null) return;
        restoring = selectedCandidate.accountStatus === 'ARCHIVED';
        input = { mode: 'EXISTING', userId: selectedCandidate.userId };
      } else {
        const matches = await platformApi.searchAdministratorCandidates(email);
        const normalizedEmail = normalizeEmailForComparison(email);
        const existing = matches.find(
          (candidate) => normalizeEmailForComparison(candidate.email) === normalizedEmail,
        );
        if (existing !== undefined) {
          setMode('EXISTING');
          setQuery(existing.email);
          setCandidates([existing]);
          setSelectedUserId(existing.eligible ? existing.userId : null);
          setError(
            existing.accountStatus === 'ARCHIVED' && existing.eligible
              ? 'Un compte supprimé existe déjà avec cette adresse. Vérifiez son identité puis utilisez « Restaurer et rattacher ».'
              : existing.eligible
                ? 'Un compte Tosho existe déjà avec cette adresse. Utilisez le compte existant plutôt que d’en créer un nouveau.'
                : 'Un compte Tosho existe déjà avec cette adresse mais ne peut pas être rattaché dans son état actuel.',
          );
          return;
        }
        input = { mode: 'NEW', givenName, familyName, email };
      }

      const { csrfToken } = await authApi.csrfToken();
      const result = await platformApi.addAdministrator(organization.id, input, csrfToken);
      if (result.invitationDelivery === 'FAILED') {
        onDone(
          restoring
            ? `Le compte supprimé a été restauré et rattaché à ${organization.displayName}, mais la nouvelle invitation d’activation n’a pas été envoyée. Vous pouvez la renvoyer depuis Administrateurs.`
            : `L’Administrateur a été créé pour ${organization.displayName}, mais l’invitation n’a pas été envoyée. Vous pouvez la renvoyer depuis Administrateurs.`,
        );
      } else if (result.invitationDelivery === 'SENT') {
        onDone(
          restoring
            ? `Le compte supprimé a été restauré et rattaché comme Administrateur de ${organization.displayName}. Une nouvelle invitation d’activation a été envoyée.`
            : `L’Administrateur a été rattaché à ${organization.displayName} et son invitation a été envoyée.`,
        );
      } else {
        onDone(
          `Le compte existant a été rattaché comme Administrateur de ${organization.displayName}.`,
        );
      }
    } catch (caught) {
      setError(provisioningErrorMessage(caught, 'Le rattachement de l’Administrateur a échoué.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop supervisor-provisioning-backdrop"
      aria-labelledby="attach-administrator-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          onClick={onClose}
          disabled={pending}
          aria-label="Fermer"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel supervisor-provisioning-dialog">
        <header className="supervisor-provisioning-dialog__header tosho-modal-header">
          <div>
            <p>{organization.displayName}</p>
            <h2 id="attach-administrator-title">Rattacher un Administrateur</h2>
          </div>
        </header>
        <form className="supervisor-provisioning-form" onSubmit={(event) => void submit(event)}>
          <div
            className="supervisor-provisioning-tabs"
            role="tablist"
            aria-label="Type d’Administrateur"
          >
            <button
              type="button"
              className={mode === 'EXISTING' ? 'is-active' : ''}
              onClick={() => {
                setMode('EXISTING');
                setError(null);
              }}
            >
              Compte existant
            </button>
            <button
              type="button"
              className={mode === 'NEW' ? 'is-active' : ''}
              onClick={() => {
                setMode('NEW');
                setError(null);
              }}
            >
              Nouvel Administrateur
            </button>
          </div>

          {mode === 'EXISTING' ? (
            <div className="supervisor-candidate-picker">
              <label>
                <span>Rechercher par nom ou e-mail</span>
                <div className="supervisor-candidate-picker__field">
                  <PrototypeIcon name="search" />
                  <input
                    type="search"
                    autoComplete="off"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.currentTarget.value);
                      setSelectedUserId(null);
                    }}
                    placeholder="Commencez à saisir…"
                  />
                </div>
              </label>
              {candidateLoading ? (
                <small className="supervisor-candidate-picker__hint">Recherche…</small>
              ) : null}
              {query.trim().length > 0 && query.trim().length < 2 ? (
                <small className="supervisor-candidate-picker__hint">
                  Saisissez au moins 2 caractères.
                </small>
              ) : null}
              {candidates.length > 0 ? (
                <ul className="supervisor-candidate-picker__suggestions" role="listbox">
                  {candidates.map((candidate) => (
                    <li key={candidate.userId}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={candidate.userId === selectedUserId}
                        className={candidate.userId === selectedUserId ? 'is-selected' : ''}
                        disabled={!candidate.eligible}
                        onClick={() => setSelectedUserId(candidate.userId)}
                      >
                        <span>
                          <strong>
                            {candidate.givenName} {candidate.familyName}
                          </strong>
                          <small>{candidate.email}</small>
                        </span>
                        <small>
                          {candidate.eligible
                            ? candidate.accountStatus === 'ARCHIVED'
                              ? 'Compte supprimé — restaurable'
                              : 'Disponible'
                            : candidate.membership
                              ? `Déjà rattaché à ${candidate.membership.organization.displayName}`
                              : 'Compte indisponible'}
                        </small>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {selectedCandidate ? (
                <p className="supervisor-candidate-picker__selected" role="status">
                  Sélection :{' '}
                  <strong>
                    {selectedCandidate.givenName} {selectedCandidate.familyName}
                  </strong>{' '}
                  · {selectedCandidate.email}
                </p>
              ) : null}
              {restoringArchived ? (
                <p className="supervisor-provisioning-form__help">
                  Ce compte supprimé sera restauré avec la même identité et son historique sera
                  conservé. Une nouvelle invitation d’activation sera envoyée : l’utilisateur devra
                  définir un nouveau mot de passe et enrôler à nouveau son authentification à deux
                  facteurs.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="supervisor-provisioning-form__grid">
              <label>
                <span>Prénom</span>
                <input
                  required
                  maxLength={100}
                  value={givenName}
                  onChange={(event) => setGivenName(event.currentTarget.value)}
                />
              </label>
              <label>
                <span>Nom</span>
                <input
                  required
                  maxLength={100}
                  value={familyName}
                  onChange={(event) => setFamilyName(event.currentTarget.value)}
                />
              </label>
              <label className="is-wide">
                <span>Adresse e-mail</span>
                <input
                  required
                  type="email"
                  autoComplete="off"
                  maxLength={320}
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                />
              </label>
              <p className="supervisor-provisioning-form__help is-wide">
                Tosho enverra le parcours d’activation standard. Aucun mot de passe ni MFA n’est
                défini par le Superviseur.
              </p>
            </div>
          )}

          {error ? (
            <p className="supervisor-danger-error tosho-feedback is-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="supervisor-provisioning-dialog__actions">
            <button
              className="supervisor-button supervisor-button--secondary"
              type="button"
              disabled={pending}
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              className="supervisor-button supervisor-button--primary"
              type="submit"
              disabled={pending || (mode === 'EXISTING' && selectedCandidate === null)}
            >
              {pending
                ? 'Rattachement…'
                : mode === 'NEW'
                  ? 'Créer et inviter'
                  : restoringArchived
                    ? 'Restaurer et rattacher'
                    : 'Rattacher le compte'}
            </button>
          </div>
        </form>
      </section>
    </ModalBackdrop>
  );
}

function normalizeEmailForComparison(candidate: string): string {
  return candidate.trim().normalize('NFKC').toLocaleLowerCase('fr-FR');
}

function provisioningErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof PlatformApiError && error.status === 409) {
    return 'Ce compte ou cette organisation a changé entre-temps. Actualisez la recherche et réessayez.';
  }
  if (error instanceof PlatformApiError && error.status === 400) {
    return 'Certaines informations sont invalides ou incomplètes.';
  }
  if (error instanceof PlatformApiError && error.status === 403) {
    return 'Votre session ne permet plus cette opération. Reconnectez-vous puis réessayez.';
  }
  return fallback;
}

function organizationTypeLabel(type: PlatformOrganizationResult['type']): string {
  switch (type) {
    case 'INDIVIDUAL':
      return 'Particulier';
    case 'ASSOCIATION':
      return 'Association';
    case 'PUBLIC_INSTITUTION':
      return 'Collectivité';
    case 'FRENCH_COMPANY':
    case 'FOREIGN_COMPANY':
      return 'Société';
    case 'OTHER':
      return 'Autre';
  }
}

function organizationStatusLabel(status: PlatformOrganizationResult['status']): string {
  switch (status) {
    case 'DRAFT':
      return 'Brouillon';
    case 'ACTIVE':
      return 'Active';
    case 'SUSPENDED':
      return 'Suspendue';
    case 'ARCHIVED':
      return 'Archivée';
  }
}
